import { useMemo, useRef, useState } from "react";
import api from "../api";
import { Field, FileInput } from "./ui/Form";

/**
 * Exhibit A to the Warrant Return (ROADMAP R1).
 *
 * The whole job in one place: drop the county's files, read them, look at what
 * the rules did, settle anything they could not, and download the Word file (to
 * edit), the PDF (to send) or the CSV.
 *
 * It is three steps because the slow part and the judgement part are different.
 * "Prepare" reads the county files and ArcGIS once and keeps every record in the
 * browser. Everything after that -- deciding a tie, comparing with last time,
 * choosing the report date -- is quick, asks the server only to apply the rules
 * to those records, and never reads the county files again. Nothing is stored on
 * the server, and the ownership data never leaves the request it came in.
 */

type Source = "upload" | "agol";

interface RawRow {
  date: string;
  address: string;
  owner: string;
  apn: string;
  activity: string;
  technician: string;
  location: string;
}

interface Candidate {
  technician: string;
  activities: string[];
  kind: string;
}

interface Decision {
  key: string;
  address: string;
  date: string;
  candidates: Candidate[];
  chosen: string;
  /** Two people did the same kind of work: a person has to say which to list. */
  tie: boolean;
  /** A tie nobody has confirmed yet. */
  needs_decision: boolean;
  reason: string;
}

interface Summary {
  inspections_found: number;
  unreadable_dates: number;
  assists_removed: number;
  work_records: number;
  same_person_duplicates_removed: number;
  other_person_records_removed: number;
  different_people_same_day: number;
  resolved_by_rule: number;
  needs_decision: number;
  properties: number;
  final_records: number;
}

type Flagged = { address: string; date: string; apn: string }[];

interface Preview {
  summary: Summary;
  decisions: Decision[];
  quality: { no_apn: Flagged; no_address: Flagged; no_owner: Flagged };
  preview: { date: string; address: string; owner: string; apn: string; technician: string; key: string }[];
  compare?: { before: number; after: number; added: { address: string; date: string }[]; removed: { address: string; date: string }[] };
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** An error from a request whose answer was meant to be a file arrives as a Blob. */
async function messageOf(err: any): Promise<string> {
  let message = err?.message || "Something went wrong.";
  const body = err?.response?.data;
  if (body instanceof Blob) {
    try {
      message = JSON.parse(await body.text()).message ?? message;
    } catch {
      /* keep the original message */
    }
  } else if (body?.message) {
    message = body.message;
  }
  return message;
}

const kindLabel = (k: string) => k.charAt(0).toUpperCase() + k.slice(1);

export default function ExhibitA() {
  const [source, setSource] = useState<Source>("upload");
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(today());
  const [ownership, setOwnership] = useState<File | null>(null);
  const [parcels, setParcels] = useState<File | null>(null);
  const [address, setAddress] = useState<File | null>(null);

  const [busy, setBusy] = useState<"" | "prepare" | "refresh" | "download">("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const [rows, setRows] = useState<RawRow[] | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [data, setData] = useState<Preview | null>(null);
  const [previousCsv, setPreviousCsv] = useState("");
  const [previousName, setPreviousName] = useState("");
  const [reportDate, setReportDate] = useState(today());
  const [showAll, setShowAll] = useState(false);
  const [showRule, setShowRule] = useState(false);
  const sizeWarned = useRef(false);

  const datesValid = Boolean(startDate && endDate && startDate <= endDate);
  const ready = Boolean(ownership) && (source === "agol" || (parcels && address)) && datesValid;
  const totalMb = useMemo(
    () => [ownership, parcels, address].reduce((n, f) => n + (f ? f.size : 0), 0) / 1048576,
    [ownership, parcels, address]
  );

  /** Step 1: read the county files and ArcGIS. The slow one. */
  const prepare = async () => {
    setBusy("prepare");
    setError("");
    setNote("");
    setProgress(0);
    setRows(null);
    setData(null);
    setChoices({});
    try {
      const form = new FormData();
      form.append("source", source);
      form.append("start_date", startDate);
      form.append("end_date", endDate);
      if (ownership) form.append("ownership_file", ownership);
      if (source === "upload") {
        if (parcels) form.append("parcels_file", parcels);
        if (address) form.append("address_file", address);
      }
      const r = await api.post("/warrant-returns/prepare", form, {
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null),
        timeout: 0,
      });
      setRows(r.data.rows);
      setData(r.data);
      if (!r.data.rows.length) setNote("No warrant inspections were found between those dates.");
    } catch (e: any) {
      setError(await messageOf(e));
    } finally {
      setBusy("");
      setProgress(null);
    }
  };

  /** Step 2 refresh: apply the rules again, with a person's choices and an earlier version to compare. */
  const refresh = async (nextChoices = choices, previous = previousCsv) => {
    if (!rows) return;
    setBusy("refresh");
    setError("");
    try {
      const r = await api.post("/warrant-returns/exhibit", {
        rows,
        choices: nextChoices,
        format: "preview",
        previous_csv: previous || undefined,
      });
      setData(r.data);
    } catch (e: any) {
      setError(await messageOf(e));
    } finally {
      setBusy("");
    }
  };

  const decide = (key: string, technician: string) => {
    const next = { ...choices, [key]: technician };
    setChoices(next);
    refresh(next);
  };

  const loadPrevious = async (file: File | null) => {
    if (!file) {
      setPreviousCsv("");
      setPreviousName("");
      refresh(choices, "");
      return;
    }
    const text = await file.text();
    setPreviousCsv(text);
    setPreviousName(file.name);
    refresh(choices, text);
  };

  /** Step 3: the documents. */
  const download = async (format: "docx" | "pdf" | "csv") => {
    if (!rows) return;
    setBusy("download");
    setError("");
    try {
      const r = await api.post(
        "/warrant-returns/exhibit",
        { rows, choices, format, report_date: reportDate },
        { responseType: "blob" }
      );
      const disposition: string = r.headers["content-disposition"] || "";
      const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1];
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = named ? decodeURIComponent(named) : `Exhibit_A_to_Warrant_Return.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setNote(`Downloaded ${link.download} (${r.headers["x-record-count"] ?? "?"} records).`);
    } catch (e: any) {
      setError(await messageOf(e));
    } finally {
      setBusy("");
    }
  };

  const s = data?.summary;
  // Ties stay on screen once chosen, so a choice can be changed; only the count of
  // unconfirmed ones goes down.
  const ties = data?.decisions.filter((d) => d.tie) ?? [];
  const pending = ties.filter((d) => d.needs_decision);
  const byRule = data?.decisions.filter((d) => !d.tie) ?? [];
  const flagged = data
    ? data.quality.no_apn.length + data.quality.no_address.length + data.quality.no_owner.length
    : 0;
  const shown = data ? (showAll ? data.preview : data.preview.slice(0, 15)) : [];

  const Count = ({ label, value, sign, strong }: { label: string; value: number; sign?: string; strong?: boolean }) => (
    <tr className={strong ? "border-t-2 border-line-strong font-bold" : "border-t border-line"}>
      <td className="px-3 py-1.5">{label}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {sign}
        {value.toLocaleString()}
      </td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <p className="text-ink-soft text-[13px]">
        Builds the Exhibit A table for the court: every property where staff worked under the warrant, one row per
        date, with the owner and parcel number. Warrant assists are left out, and a property is one row per day.
        Read the county files once, check the counts, then download the Word file to edit or the PDF to send.
      </p>

      {/* ---------------- Step 1 ---------------- */}
      <div className="ui-card p-4 space-y-4">
        <div className="flex items-center gap-2">
          <span className="pill bg-brand-tint text-brand-deep">1</span>
          <h3 className="font-extrabold text-[15px]">Load the files</h3>
        </div>

        <div>
          <div className="eyebrow mb-1.5">Parcels &amp; addresses from</div>
          <div className="flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold w-fit">
            {(
              [
                ["upload", "The county's files"],
                ["agol", "Layers already on ArcGIS Online"],
              ] as [Source, string][]
            ).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setSource(val)}
                disabled={busy !== ""}
                className={`px-3 py-1 rounded-sm2 transition ${
                  source === val ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-ink-faint text-[12px] mt-1.5">
            {source === "upload"
              ? "Use this each year after the county sends new data. The parcels and addresses come from the county's public data hub; the ownership file comes from the county GIS team."
              : "Uses the CMAD parcels and addresses already published to ArcGIS Online. Only right if those were refreshed from this year's county data."}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 max-w-md">
          <label className="block">
            <span className="eyebrow">Warrant inspections from</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="eyebrow">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm"
            />
          </label>
        </div>
        {startDate && endDate && startDate > endDate && (
          <p className="text-[12px] text-red-600 -mt-2">The end date is before the start date.</p>
        )}

        <div className="space-y-3">
          <Field
            label="Ownership file from the county"
            hint="The CSV, or the ZIP as the county sends it. It is read for this run only and never kept."
          >
            <FileInput accept=".csv,.zip" file={ownership} onFile={setOwnership} disabled={busy !== ""} />
          </Field>
          {source === "upload" && (
            <div className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-3">
              <Field label="Parcels (shapefile .zip)">
                <FileInput accept=".zip" file={parcels} onFile={setParcels} disabled={busy !== ""} />
              </Field>
              <Field label="Addresses (shapefile .zip)">
                <FileInput accept=".zip" file={address} onFile={setAddress} disabled={busy !== ""} />
              </Field>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={prepare} disabled={!ready || busy !== ""} className="btn-primary">
            {busy === "prepare" ? "Working…" : rows ? "Read the files again" : "Read the files and prepare"}
          </button>
          {totalMb > 0 && <span className="text-ink-faint text-[12px]">{totalMb.toFixed(0)} MB to upload</span>}
        </div>

        {busy === "prepare" && (
          <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-paper-2 overflow-hidden">
              <div
                className="h-full bg-brand transition-all"
                style={{ width: `${progress === null ? 100 : progress}%` }}
              />
            </div>
            <p className="text-ink-soft text-[13px]">
              {progress !== null && progress < 100
                ? `Uploading… ${progress}%`
                : "Reading the county files and ArcGIS. This takes several minutes: matching each inspection to its parcel, address and owner is the slow part. Leave this page open."}
            </p>
          </div>
        )}
      </div>

      {error && <div className="rounded-sm2 p-3 text-sm bg-red-50 border border-red-200 text-red-800">{error}</div>}
      {note && !error && <div className="rounded-sm2 p-3 text-sm bg-brand-tint text-brand-deep">{note}</div>}

      {/* ---------------- Step 2 ---------------- */}
      {data && s && s.inspections_found > 0 && (
        <div className="ui-card p-4 space-y-5">
          <div className="flex items-center gap-2">
            <span className="pill bg-brand-tint text-brand-deep">2</span>
            <h3 className="font-extrabold text-[15px]">Check it</h3>
            {busy === "refresh" && <span className="text-ink-faint text-[12px]">updating…</span>}
          </div>

          {/* The counts: before and after, so nobody re-reads the list to find out what changed. */}
          <div>
            <div className="eyebrow mb-1.5">What was done to the records</div>
            <div className="rounded-sm2 border border-line overflow-hidden max-w-lg">
              <table className="w-full text-[13px]">
                <tbody>
                  <Count label="Warrant inspections found" value={s.inspections_found} />
                  <Count label="Warrant assists, left out" value={s.assists_removed} sign="− " />
                  <Count label="Same person, same property, same day" value={s.same_person_duplicates_removed} sign="− " />
                  <Count label="Another person, same property, same day" value={s.other_person_records_removed} sign="− " />
                  <Count label="Records in Exhibit A" value={s.final_records} strong />
                </tbody>
              </table>
            </div>
            <p className="text-ink-faint text-[12px] mt-1">
              {s.properties.toLocaleString()} properties.
              {s.unreadable_dates > 0 && ` ${s.unreadable_dates} record(s) had no readable date and were left out.`}
            </p>
          </div>

          {/* Ties: the only judgement the tool will not make silently. */}
          {s.different_people_same_day > 0 && (
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <div className="eyebrow">Different people, same property, same day</div>
                <span className="text-ink-faint text-[12px]">
                  {s.different_people_same_day} case{s.different_people_same_day === 1 ? "" : "s"}:{" "}
                  {s.resolved_by_rule} settled by the rule, {s.needs_decision} still need{s.needs_decision === 1 ? "s" : ""} you
                </span>
              </div>

              {ties.length > 0 && (
                <div
                  className={`mt-2 rounded-sm2 border p-3 space-y-3 ${
                    pending.length > 0 ? "border-amber-300 bg-amber-50" : "border-brand-tint bg-brand-tint/40"
                  }`}
                >
                  <p className={`text-[13px] font-semibold ${pending.length > 0 ? "text-amber-900" : "text-brand-deep"}`}>
                    {pending.length > 0
                      ? "Two people did the same kind of work, so the tool cannot say which to list. Pick one for each (the tool's pick is selected):"
                      : "Every tie is confirmed. You can still change one."}
                  </p>
                  {ties.map((d) => (
                    <div key={d.key} className="text-[13px]">
                      <div className="font-semibold">
                        {d.address} <span className="font-normal text-ink-soft">· {d.date}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1">
                        {d.candidates.map((c) => (
                          <label key={c.technician} className="flex items-start gap-1.5">
                            <input
                              type="radio"
                              name={d.key}
                              checked={d.chosen === c.technician}
                              onChange={() => decide(d.key, c.technician)}
                              disabled={busy !== ""}
                              className="mt-1"
                            />
                            <span>
                              {c.technician}
                              <span className="block text-[11px] text-ink-faint">
                                {c.activities.map((a) => a.replace(/^WARRANT /, "").toLowerCase()).join(", ")}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={() => setShowRule((v) => !v)} className="mt-2 text-[12px] text-ink-soft hover:text-ink font-semibold">
                {showRule ? "▾" : "▸"} {byRule.length} settled by the rule
              </button>
              {showRule && (
                <div className="mt-1 rounded-sm2 border border-line overflow-hidden">
                  <table className="w-full text-[12px]">
                    <tbody>
                      {byRule.map((d) => (
                        <tr key={d.key} className="border-t border-line first:border-t-0 align-top">
                          <td className="px-3 py-1.5">
                            {d.address} <span className="text-ink-faint">· {d.date}</span>
                          </td>
                          <td className="px-3 py-1.5">
                            Listed: <strong>{d.chosen}</strong>
                            <span className="block text-ink-faint">{d.reason}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Records the document cannot be trusted on. */}
          {flagged > 0 && (
            <div className="rounded-sm2 border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900">
              <div className="font-semibold">Look at these before sending</div>
              {(
                [
                  ["no_owner", "no owner found in the county's ownership file"],
                  ["no_apn", "not matched to a parcel"],
                  ["no_address", "no address"],
                ] as const
              ).map(([k, label]) =>
                data.quality[k].length ? (
                  <div key={k} className="mt-1">
                    <span className="font-semibold">{data.quality[k].length}</span> with {label}:{" "}
                    <span className="text-[12px]">
                      {data.quality[k].slice(0, 8).map((f) => `${f.address || "(no address)"} ${f.date}`).join("; ")}
                      {data.quality[k].length > 8 ? ` … and ${data.quality[k].length - 8} more` : ""}
                    </span>
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Compare with an earlier version. */}
          <div>
            <div className="eyebrow mb-1.5">Compare with an earlier version (optional)</div>
            <div className="max-w-md">
              <FileInput
                accept=".csv"
                file={previousName ? new File([], previousName) : null}
                onFile={loadPrevious}
                disabled={busy !== ""}
              />
            </div>
            <p className="text-ink-faint text-[12px] mt-1">
              Choose the CSV from a version someone has already checked, and this lists only what changed.
            </p>
            {data.compare && (
              <div className="mt-2 text-[13px]">
                <p>
                  Before: <strong>{data.compare.before}</strong> records · Now: <strong>{data.compare.after}</strong> records ·{" "}
                  {data.compare.added.length} added · {data.compare.removed.length} removed
                </p>
                {(data.compare.added.length > 0 || data.compare.removed.length > 0) && (
                  <div className="grid gap-3 sm:grid-cols-2 mt-1 text-[12px]">
                    <div>
                      <div className="font-semibold text-brand-deep">Added</div>
                      {data.compare.added.map((a, i) => (
                        <div key={i}>{a.address} · {a.date}</div>
                      ))}
                      {!data.compare.added.length && <div className="text-ink-faint">none</div>}
                    </div>
                    <div>
                      <div className="font-semibold text-red-700">Removed</div>
                      {data.compare.removed.map((a, i) => (
                        <div key={i}>{a.address} · {a.date}</div>
                      ))}
                      {!data.compare.removed.length && <div className="text-ink-faint">none</div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The records themselves. */}
          <div>
            <div className="eyebrow mb-1.5">Exhibit A</div>
            <div className="rounded-sm2 border border-line overflow-auto max-h-[26rem]">
              <table className="w-full text-[12px]">
                <thead className="bg-paper-2 sticky top-0">
                  <tr>
                    {["Date", "Property Address", "Owner", "APN", "Technician"].map((h) => (
                      <th key={h} className="px-3 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.key} className="border-t border-line">
                      <td className="px-3 py-1 whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-1">{r.address || "—"}</td>
                      <td className="px-3 py-1">{r.owner || "—"}</td>
                      <td className="px-3 py-1 font-mono">{r.apn || "—"}</td>
                      <td className="px-3 py-1 whitespace-nowrap">{r.technician}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.preview.length > 15 && (
              <button onClick={() => setShowAll((v) => !v)} className="mt-1 text-[12px] text-ink-soft hover:text-ink font-semibold">
                {showAll ? "Show the first 15" : `Show all ${data.preview.length}`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Step 3 ---------------- */}
      {data && s && s.final_records > 0 && (
        <div className="ui-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="pill bg-brand-tint text-brand-deep">3</span>
            <h3 className="font-extrabold text-[15px]">Download</h3>
          </div>
          <label className="block max-w-[14rem]">
            <span className="eyebrow">Date on the document</span>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => download("docx")} disabled={busy !== ""} className="btn-primary">
              Word (to edit)
            </button>
            <button onClick={() => download("pdf")} disabled={busy !== ""} className="btn-primary">
              PDF (to send)
            </button>
            <button onClick={() => download("csv")} disabled={busy !== ""} className="btn-ghost">
              CSV
            </button>
          </div>
          <p className="text-ink-faint text-[12px]">
            The Word file is the editable one: change a font, fix an address, then save it as a PDF from Word. The PDF here
            is laid out the same way for sending as it stands. If an owner name or address is wrong, fix it in the county
            file or in FieldSeeker and read the files again; a fix made only in Access is not seen here.
          </p>
          {pending.length > 0 && (
            <p className="text-[12px] text-amber-800">
              {pending.length} tie{pending.length === 1 ? " is" : "s are"} still on the tool's default pick. Confirm them above
              first, or the document lists whoever the tool chose.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
